import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AACModule } from './modules/AACModule';
import { SpeechTherapyModule } from './modules/SpeechTherapyModule';
import { AutismABAModule } from './modules/AutismABAModule';
import { SignLanguageModule } from './modules/SignLanguageModule';
import { BrailleModule } from './modules/BrailleModule';
import { StudentIEPModule } from './modules/StudentIEPModule';
import { TherapistSOAPModule } from './modules/TherapistSOAPModule';
import { TeacherClassroomModule } from './modules/TeacherClassroomModule';
import { AssessmentModule } from './modules/AssessmentModule';
import { SchoolERPModule } from './modules/SchoolERPModule';
import { HospitalRehabModule } from './modules/HospitalRehabModule';
import { NDISBillingModule } from './modules/NDISBillingModule';
import { ParentCommModule } from './modules/ParentCommModule';
import { InventoryModule } from './modules/InventoryModule';
import { AnalyticsBIModule } from './modules/AnalyticsBIModule';
import { AICopilotModule } from './modules/AICopilotModule';
import { SensoryRoomModule } from './modules/SensoryRoomModule';
import { TeleRehabModule } from './modules/TeleRehabModule';
import { EarlyInterventionModule } from './modules/EarlyInterventionModule';
import { VocationalLivingModule } from './modules/VocationalLivingModule';
import { HydrotherapyModule } from './modules/HydrotherapyModule';
import { MusicTherapyModule } from './modules/MusicTherapyModule';
import { AnimalTherapyModule } from './modules/AnimalTherapyModule';
import { ProstheticsModule } from './modules/ProstheticsModule';
import { VBMAPPModule } from './modules/VBMAPPModule';
import { CoreVocabAIModule } from './modules/CoreVocabAIModule';
import { FIMScoringModule } from './modules/FIMScoringModule';
import { EVVGPSModule } from './modules/EVVGPSModule';
import { GamifiedNeuroModule } from './modules/GamifiedNeuroModule';
import { TransitionMatrixModule } from './modules/TransitionMatrixModule';

import { 
  Volume2, Mic, Brain, Eye, Compass, GraduationCap, Stethoscope, 
  Users, ClipboardList, Layers, Activity, CreditCard, MessageCircle, 
  Box, TrendingUp, Bot, Grid, Download, Smartphone, Monitor, Apple, Sparkles, Lock, ShieldCheck, Sun, Video, Baby, Briefcase, Waves, Music, Heart, Cpu, BarChart2, Zap, ClipboardCheck, MapPin, Gamepad2
} from 'lucide-react';
import { UserSession } from '../AppShell';

interface ModuleExplorerProps {
  initialModule?: string;
  currentUser?: UserSession | null;
}

export const ModuleExplorer: React.FC<ModuleExplorerProps> = ({ initialModule = 'aac', currentUser }) => {
  const { t, i18n } = useTranslation();
  const [activeModule, setActiveModule] = useState(initialModule);
  const [downloadSuccess, setDownloadSuccess] = useState('');

  const modules = [
    { id: 'aac', label: t('mod_aac'), filePrefix: 'AAC_SpeechSynthesizer', icon: Volume2, category: 'Assistive Communication' },
    { id: 'speech-therapy', label: t('mod_speech'), filePrefix: 'SpeechTherapy_Articulation', icon: Mic, category: 'Clinical Therapy' },
    { id: 'autism', label: t('mod_autism'), filePrefix: 'Autism_ABA_Behavior', icon: Brain, category: 'Behavioral Health' },
    { id: 'sign-language', label: t('mod_sign'), filePrefix: 'SignLanguage_Gestures', icon: Eye, category: 'Visual / Hearing' },
    { id: 'braille', label: t('mod_braille'), filePrefix: 'Braille_Tactile_Learning', icon: Compass, category: 'Visual Impairment' },
    { id: 'student', label: t('mod_student'), filePrefix: 'Student_IEP_Progress', icon: GraduationCap, category: 'Special Education' },
    { id: 'therapist', label: t('mod_therapist'), filePrefix: 'Therapist_SOAP_Notes', icon: Stethoscope, category: 'Clinical Operations' },
    { id: 'teacher', label: t('mod_teacher'), filePrefix: 'Teacher_Classroom_Lesson', icon: Users, category: 'School Education' },
    { id: 'assessment', label: t('mod_assessment'), filePrefix: 'Diagnostic_Clinical_Assessment', icon: ClipboardList, category: 'Evaluations' },
    { id: 'school-erp', label: t('mod_erp'), filePrefix: 'SpecialSchool_ERP_Timetable', icon: Layers, category: 'Administration' },
    { id: 'hospital', label: t('mod_hospital'), filePrefix: 'RehabHospital_PatientCare', icon: Activity, category: 'Inpatient Care' },
    { id: 'billing', label: t('mod_billing'), filePrefix: 'NDIS_Clinical_Billing', icon: CreditCard, category: 'Finance' },
    { id: 'communication', label: t('mod_comm'), filePrefix: 'ParentComm_Messenger', icon: MessageCircle, category: 'Family Engagement' },
    { id: 'inventory', label: t('mod_inventory'), filePrefix: 'AssistiveDevice_Inventory', icon: Box, category: 'Resources' },
    { id: 'analytics', label: t('mod_analytics'), filePrefix: 'ExecutiveAnalytics_BI', icon: TrendingUp, category: 'Reporting' },
    { id: 'ai', label: t('mod_ai'), filePrefix: 'AICopilot_ReportSummarizer', icon: Bot, category: 'Intelligence' },
    { id: 'sensory-room', label: t('mod_sensory'), filePrefix: 'SensoryRoom_Snoezelen_Controller', icon: Sun, category: 'Environmental' },
    { id: 'tele-rehab', label: t('mod_tele'), filePrefix: 'TeleRehab_RemoteVideo_Therapy', icon: Video, category: 'Tele-Health' },
    { id: 'early-intervention', label: t('mod_early'), filePrefix: 'EarlyIntervention_Developmental_Tracker', icon: Baby, category: 'Pediatrics' },
    { id: 'vocational-living', label: t('mod_vocal'), filePrefix: 'Vocational_IndependentLiving_Skills', icon: Briefcase, category: 'Adult Rehab' },
    { id: 'hydrotherapy', label: t('mod_hydro'), filePrefix: 'Hydrotherapy_AquaticRehab_Controller', icon: Waves, category: 'Physical Rehab' },
    { id: 'music-therapy', label: t('mod_music'), filePrefix: 'MusicTherapy_RhythmicAuditory_Stimulation', icon: Music, category: 'Neuro-Rehab' },
    { id: 'animal-therapy', label: t('mod_animal'), filePrefix: 'AnimalAssisted_Therapy_PetCare', icon: Heart, category: 'Emotional' },
    { id: 'prosthetics', label: t('mod_prosthetics'), filePrefix: 'Prosthetics_Orthotics_Exoskeleton_Fitting', icon: Cpu, category: 'Bionics' },
    { id: 'vbmapp-assessment', label: t('mod_vbmapp'), filePrefix: 'VBMAPP_Behavioral_Assessment_Protocol', icon: BarChart2, category: 'Behavioral Health' },
    { id: 'core-vocab-ai', label: t('mod_core_vocab'), filePrefix: 'CoreVocab_MotorPlanning_AI_Predictor', icon: Zap, category: 'AAC AI' },
    { id: 'fim-weefim', label: t('mod_fim'), filePrefix: 'FIM_WeeFIM_Clinical_Scoring', icon: ClipboardCheck, category: 'Clinical Rehab' },
    { id: 'evv-gps-tracker', label: t('mod_evv'), filePrefix: 'EVV_GPS_Caregiver_CheckIn', icon: MapPin, category: 'Compliance' },
    { id: 'gamified-neuro', label: t('mod_neuro'), filePrefix: 'Gamified_WebGL_Neuro_Rehab', icon: Gamepad2, category: 'Gamified Rehab' },
    { id: 'transition-matrix', label: t('mod_transition'), filePrefix: 'Secondary_Transition_Vocational_Matrix', icon: Compass, category: 'Post-Secondary' }
  ];

  const isModuleEnabledForTenant = (moduleId: string) => {
    if (!currentUser || currentUser.role === 'SUPER_ADMIN') return true;
    if (!currentUser.enabledModules) return true;
    return currentUser.enabledModules.includes(moduleId);
  };

  const currentMod = modules.find((m) => m.id === activeModule) || modules[0];
  const isCurrentModEnabled = isModuleEnabledForTenant(activeModule);

  const handleDownloadApp = (filename: string, name: string) => {
    setDownloadSuccess(`Downloading ${name}... check your downloads folder!`);
    const link = document.createElement('a');
    link.href = `/downloads/${filename}`;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setTimeout(() => setDownloadSuccess(''), 4000);
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto py-4 px-2">
      
      {/* Logged in Tenant Subscription Status Banner */}
      {currentUser && currentUser.role !== 'SUPER_ADMIN' && (
        <div className="bg-gradient-to-r from-emerald-900 via-blue-900 to-indigo-900 text-white p-4 rounded-2xl border border-emerald-500/30 flex flex-col md:flex-row items-center justify-between gap-4 shadow-md">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-emerald-500/20 text-emerald-300 rounded-xl border border-emerald-400/30">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div>
              <div className="text-xs font-black text-white flex items-center gap-2">
                <span>{currentUser.tenant} Subscription Active</span>
                <span className="px-2 py-0.5 bg-emerald-500/30 text-emerald-200 text-[10px] font-extrabold rounded-full">
                  {currentUser.enabledModules?.length || 30} / 30 Modules Authorized by Super Admin
                </span>
              </div>
              <div className="text-[11px] text-blue-200">Logged in as {currentUser.name} ({currentUser.role}). All 30 modules are active and ready.</div>
            </div>
          </div>
        </div>
      )}

      {/* Module Explorer Header & Tab Grid */}
      <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-md space-y-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b pb-4">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-50 text-blue-700 border border-blue-200 text-xs font-bold rounded-full uppercase tracking-wider">
              <Grid className="w-3.5 h-3.5" /> 30-Module Suite Interactive Workspace
            </div>
            <h2 className="text-2xl font-black text-slate-900 mt-2">
              Select Any Authorized Module to Launch Demo
            </h2>
          </div>

          <div className="text-xs text-slate-500 font-bold bg-slate-100 px-3 py-1.5 rounded-xl self-start md:self-auto">
            Currently Selected: <span className="text-blue-600 font-extrabold">{currentMod.label}</span>
          </div>
        </div>

        {downloadSuccess && (
          <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-bold rounded-xl shadow-sm">
            {downloadSuccess}
          </div>
        )}

        {/* 30 MODULE TABS GRID */}
        <div className="grid grid-cols-2 sm:grid-cols-5 lg:grid-cols-10 gap-2 pt-2">
          {modules.map((m, idx) => {
            const Icon = m.icon;
            const isActive = activeModule === m.id;
            const isEnabled = isModuleEnabledForTenant(m.id);
            const numStr = (idx + 1).toString().padStart(2, '0');

            return (
              <button
                key={m.id}
                onClick={() => setActiveModule(m.id)}
                className={`p-2 rounded-2xl text-left transition-all duration-200 border flex flex-col justify-between space-y-1.5 relative ${
                  isActive 
                    ? 'bg-gradient-to-br from-blue-600 to-indigo-700 text-white border-blue-600 shadow-lg shadow-blue-500/25 scale-[1.03] z-10' 
                    : isEnabled
                      ? 'bg-slate-50 text-slate-700 hover:bg-slate-100 border-slate-200'
                      : 'bg-slate-100/60 text-slate-400 border-slate-200 hover:bg-slate-100'
                }`}
              >
                <div className="flex items-center justify-between w-full">
                  <Icon className={`w-3.5 h-3.5 ${isActive ? 'text-amber-300' : isEnabled ? 'text-blue-600' : 'text-slate-400'}`} />
                  
                  {!isEnabled ? (
                    <Lock className="w-3 h-3 text-amber-600" title="Module Disabled by Super Admin" />
                  ) : (
                    <span className={`text-[8px] font-mono px-1 py-0.2 rounded font-extrabold ${isActive ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-600'}`}>
                      {numStr}
                    </span>
                  )}
                </div>
                <div className="font-extrabold text-[10px] leading-tight truncate w-full flex items-center justify-between">
                  <span>{m.label.replace(/^\d+\.\s*/, '')}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* DEDICATED APP DOWNLOAD STRIP FOR SELECTED MODULE */}
      <div className="bg-gradient-to-r from-slate-900 via-blue-950 to-slate-900 text-white p-4 rounded-2xl border border-slate-800 shadow-md flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-blue-500/20 border border-blue-400/30 text-amber-400 rounded-xl">
            <Sparkles className="w-5 h-5" />
          </div>
          <div>
            <div className="text-xs font-extrabold text-white">Standalone Native Apps for {currentMod.label}</div>
            <div className="text-[11px] text-slate-300">Download standalone installer packages built specifically for this module.</div>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => handleDownloadApp(`AbilityOS_${currentMod.filePrefix}_Android.apk`, `${currentMod.label} Android APK`)}
            className="px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-xl text-xs flex items-center gap-1.5 shadow transition"
          >
            <Smartphone className="w-3.5 h-3.5" />
            <span>Android (.APK)</span>
          </button>

          <button
            onClick={() => handleDownloadApp(`AbilityOS_${currentMod.filePrefix}_Windows_Setup.exe`, `${currentMod.label} Windows Setup`)}
            className="px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white font-bold rounded-xl text-xs flex items-center gap-1.5 shadow transition"
          >
            <Monitor className="w-3.5 h-3.5" />
            <span>Win (.EXE)</span>
          </button>

          <button
            onClick={() => handleDownloadApp(`AbilityOS_${currentMod.filePrefix}_macOS.dmg`, `${currentMod.label} macOS App`)}
            className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-white font-bold rounded-xl text-xs flex items-center gap-1.5 shadow transition border border-white/20"
          >
            <Apple className="w-3.5 h-3.5" />
            <span>macOS (.DMG)</span>
          </button>
        </div>
      </div>

      {/* DYNAMIC WORKING DEMO OR LOCKED SUBSCRIPTION VIEW */}
      {!isCurrentModEnabled ? (
        <div className="bg-white rounded-3xl border border-amber-200 p-12 text-center space-y-4 shadow-xl">
          <div className="w-16 h-16 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center mx-auto">
            <Lock className="w-8 h-8" />
          </div>
          <h3 className="text-2xl font-black text-slate-900">Module Subscription Not Enabled</h3>
          <p className="text-slate-600 text-sm max-w-md mx-auto">
            The <span className="font-bold text-blue-600">{currentMod.label}</span> is not included in your tenant's current subscription plan.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-3xl border border-slate-200 shadow-xl p-2">
          {activeModule === 'aac' && <AACModule />}
          {activeModule === 'speech-therapy' && <SpeechTherapyModule />}
          {activeModule === 'autism' && <AutismABAModule />}
          {activeModule === 'sign-language' && <SignLanguageModule />}
          {activeModule === 'braille' && <BrailleModule />}
          {activeModule === 'student' && <StudentIEPModule />}
          {activeModule === 'therapist' && <TherapistSOAPModule />}
          {activeModule === 'teacher' && <TeacherClassroomModule />}
          {activeModule === 'assessment' && <AssessmentModule />}
          {activeModule === 'school-erp' && <SchoolERPModule />}
          {activeModule === 'hospital' && <HospitalRehabModule />}
          {activeModule === 'billing' && <NDISBillingModule />}
          {activeModule === 'communication' && <ParentCommModule />}
          {activeModule === 'inventory' && <InventoryModule />}
          {activeModule === 'analytics' && <AnalyticsBIModule />}
          {activeModule === 'ai' && <AICopilotModule />}
          {activeModule === 'sensory-room' && <SensoryRoomModule />}
          {activeModule === 'tele-rehab' && <TeleRehabModule />}
          {activeModule === 'early-intervention' && <EarlyInterventionModule />}
          {activeModule === 'vocational-living' && <VocationalLivingModule />}
          {activeModule === 'hydrotherapy' && <HydrotherapyModule />}
          {activeModule === 'music-therapy' && <MusicTherapyModule />}
          {activeModule === 'animal-therapy' && <AnimalTherapyModule />}
          {activeModule === 'prosthetics' && <ProstheticsModule />}
          {activeModule === 'vbmapp-assessment' && <VBMAPPModule />}
          {activeModule === 'core-vocab-ai' && <CoreVocabAIModule />}
          {activeModule === 'fim-weefim' && <FIMScoringModule />}
          {activeModule === 'evv-gps-tracker' && <EVVGPSModule />}
          {activeModule === 'gamified-neuro' && <GamifiedNeuroModule />}
          {activeModule === 'transition-matrix' && <TransitionMatrixModule />}
        </div>
      )}

    </div>
  );
};
